
import React from "react";
import { AlertTriangle, Camera, VideoOff, RefreshCcw, User, AlertCircle, Monitor, FileText, Brain, Loader2, CheckCircle, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const CameraPermissionDialog = ({ 
  open, 
  onPermissionGranted, 
  onPermissionDenied, 
  initializing, 
  required = false 
}) => (
  <Dialog open={open} onOpenChange={(open) => {
    // Only allow closing if not required
    if (!open && !required) {
      onPermissionDenied();
    }
  }}>
    <DialogContent hideCloseButton={true}>
      <DialogHeader>
        <DialogTitle>Camera Permission Required</DialogTitle>
        <DialogDescription>
          This exam requires camera access for proctoring. Please click "Allow Camera Access" to proceed with the exam.
        </DialogDescription>
      </DialogHeader>
      <div className="flex justify-center py-6">
        <Camera className="h-16 w-16 text-primary" />
      </div>
      <DialogFooter>
        <Button onClick={onPermissionGranted} disabled={initializing}>
          {initializing ? "Accessing Camera..." : "Allow Camera Access"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const FaceNotDetectedDialog = ({ 
  open, 
  onRetryCamera, 
  retryingCamera 
}) => (
  <Dialog open={open} onOpenChange={() => {}}>
    <DialogContent hideCloseButton={true}>
      <DialogHeader>
        <DialogTitle className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          Face Not Detected
        </DialogTitle>
        <DialogDescription>
          Your face is not visible to the camera. Please adjust your position to ensure your face is clearly visible.
          <div className="mt-4 text-red-500">
            You cannot proceed with the exam until your face is detected.
          </div>
        </DialogDescription>
      </DialogHeader>
      <div className="flex justify-center py-4">
        <User className="h-16 w-16 text-red-500" />
      </div>
      <DialogFooter>
        <Button onClick={onRetryCamera} disabled={retryingCamera}>
          {retryingCamera ? "Reconnecting Camera..." : "Retry Camera Connection"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ScreenCapturePermissionDialog = ({ 
  open, 
  onPermissionGranted, 
  onPermissionDenied, 
  initializing, 
  required = false 
}) => (
  <Dialog open={open} onOpenChange={(open) => {
    // Only allow closing if not required
    if (!open && !required) {
      onPermissionDenied();
    }
  }}>
    <DialogContent hideCloseButton={true} className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="text-xl">🖥️ Screen Sharing Permission Required</DialogTitle>
        <DialogDescription className="space-y-4">
          <p>This exam requires screen sharing for proctoring. When you click "Allow Screen Sharing", your browser will show a dialog.</p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">What to expect:</h4>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Click "Allow Screen Sharing" below</li>
              <li>Browser will show screen sharing options</li>
              <li>Select the "Entire screen" tab</li>
              <li>Choose your main screen from the preview</li>
              <li>Click "Share" (NOT Cancel)</li>
            </ol>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> If you select the wrong option, you'll be asked to try again. 
              This ensures exam integrity and security.
            </p>
          </div>
        </DialogDescription>
      </DialogHeader>
      
      <div className="flex justify-center py-4">
        <div className="text-center">
          <Monitor className="h-20 w-20 text-primary mx-auto mb-2" />
          <p className="text-sm text-gray-600">Your entire screen will be monitored</p>
        </div>
      </div>
      
      <DialogFooter className="flex-col space-y-2">
        <Button 
          onClick={onPermissionGranted} 
          disabled={initializing}
          className="w-full bg-green-600 hover:bg-green-700"
          size="lg"
        >
          {initializing ? (
            <>
              <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
              Setting up screen sharing...
            </>
          ) : (
            <>
              <Monitor className="h-4 w-4 mr-2" />
              Allow Screen Sharing (Entire Screen Only)
            </>
          )}
        </Button>
        
        {!required && (
          <Button 
            variant="outline" 
            onClick={onPermissionDenied}
            className="w-full"
          >
            Skip Screen Capture
          </Button>
        )}
        
        <p className="text-xs text-gray-500 text-center mt-2">
          Screen sharing is required for exam proctoring and security
        </p>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const WarningDialog = ({ 
  open, 
  onAcknowledge, 
  warningMessage, 
  warningCount, 
  warningsAllowed
}) => (
  <AlertDialog open={open} onOpenChange={(open) => !open && onAcknowledge()}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center">
          <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
          Warning {warningCount}/{warningsAllowed || "∞"}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {warningMessage}
          {warningsAllowed > 0 && warningCount + 1 >= warningsAllowed && (
            <div className="mt-2 text-red-500 font-semibold">
              This is your last warning. Any further violations will result
              in disqualification.
            </div>
          )}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogAction onClick={onAcknowledge}>
          I Understand
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const ResumeExtractionBuffer = ({ 
  extractingResume, 
  noResumesFound, 
  extractionAttempted,
  candidateCount = 1,
  onRetry,
  onUploadResumes
}) => {
  if (noResumesFound) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6">
        <div className="max-w-md w-full text-center space-y-6">
          {/* Error Icon */}
          <div className="mx-auto w-20 h-20 bg-red-50 rounded-full flex items-center justify-center">
            <AlertCircle className="h-10 w-10 text-red-500" />
          </div>
          
          {/* Error Message */}
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-gray-900">No Resume Files Found</h3>
            <p className="text-gray-600">
              We couldn't find resume files for the selected candidate{candidateCount > 1 ? 's' : ''}. 
              Please ensure resume files are uploaded before proceeding.
            </p>
          </div>
          
          {/* Retry Button */}
          <div className="flex items-center gap-3">
            {onRetry && (
              <Button 
                onClick={onRetry}
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCcw className="h-4 w-4" />
                Try Again
              </Button>
            )}
            
            {onUploadResumes && (
              <Button 
                onClick={onUploadResumes}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                <Upload className="h-4 w-4" />
                Upload Resume Files
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (extractingResume || (!extractionAttempted && candidateCount > 0)) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-95 z-50">
        <div className="max-w-md w-full mx-4">
          {/* Progress Bar */}
          <div className="space-y-4 text-center">
            <div className="text-sm text-gray-600">
              <div>Resume analysis will be updated due to candidate changes</div>
              <div className="mt-2">{extractingResume ? 'Processing...' : 'Preparing...'}</div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-green-500 to-blue-500 rounded-full transition-all duration-1000 ease-out"
                   style={{ width: extractingResume ? '40%' : '20%' }}>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
